import { useEffect, useMemo, useRef, useState } from "react";

import { ErrorIcon } from "../../../assets/icons/errorIcon";
import { useNearViewport } from "../../../hooks/useNearViewport";
import { LoaderWithText } from "../../../shared/Loader";
import ImageViewModal from "../modals/ImageViewModal";

const IMAGE_LOAD_TIMEOUT_MS = 20000;

const normalizeImageSrc = (src?: string) => {
	if (!src) {
		return "";
	}

	const trimmedSrc = src.trim();
	if (trimmedSrc.length === 0) {
		return "";
	}

	if (
		trimmedSrc.startsWith('"') &&
		trimmedSrc.endsWith('"')
	) {
		return trimmedSrc.slice(1, -1);
	}

	return trimmedSrc;
};

export default function ImageRenderer({
	src,
	alt,
}: {
	src?: string;
	alt?: string;
}) {
	const normalizedSrc = useMemo(
		() => normalizeImageSrc(src),
		[src]
	);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [error, setError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const loadTimeoutRef = useRef<number | null>(null);
	const { containerRef, isNearViewport } =
		useNearViewport();

	const clearLoadTimeout = () => {
		if (loadTimeoutRef.current !== null) {
			window.clearTimeout(loadTimeoutRef.current);
			loadTimeoutRef.current = null;
		}
	};

	useEffect(() => {
		clearLoadTimeout();

		if (!normalizedSrc) {
			setError(true);
			setIsLoading(false);
			return;
		}

		if (!isNearViewport) {
			return;
		}

		setError(false);
		setIsLoading(true);
		loadTimeoutRef.current = window.setTimeout(() => {
			setError(true);
			setIsLoading(false);
		}, IMAGE_LOAD_TIMEOUT_MS);

		return () => {
			clearLoadTimeout();
		};
	}, [isNearViewport, normalizedSrc]);

	const handleImageLoad = () => {
		clearLoadTimeout();
		setIsLoading(false);
	};

	const handleImageError = () => {
		clearLoadTimeout();
		setError(true);
		setIsLoading(false);
	};

	if (error || !normalizedSrc) {
		return (
			<div className="image-error">
				<ErrorIcon />
				<p>Failed to load image.</p>
			</div>
		);
	}

	if (!isNearViewport) {
		return (
			<div
				ref={containerRef}
				className="artifact-deferred-placeholder"
			>
				<span className="artifact-deferred-placeholder__eyebrow">
					Image artifact
				</span>
				<p className="artifact-deferred-placeholder__title">
					Image preview will load when you scroll here.
				</p>
				{alt && (
					<p className="artifact-deferred-placeholder__body">
						{alt}
					</p>
				)}
			</div>
		);
	}

	return (
		<>
			<div
				className={`image-container ${
					isLoading ? "image-container--loading" : ""
				}`.trim()}
				onClick={() => {
					if (!isLoading) {
						setIsModalOpen(true);
					}
				}}
				aria-busy={isLoading}
			>
				{isLoading && (
					<div className="image-loading-overlay">
						<LoaderWithText />
					</div>
				)}
				<img
					src={normalizedSrc}
					alt={alt ?? "Image artifact"}
					className="image-preview"
					loading="eager"
					onLoad={handleImageLoad}
					onError={handleImageError}
				/>
				{!isLoading && (
					<div className="image-hover-overlay">
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M15 3H21M21 3V9M21 3L13 11M9 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V15"
								stroke="white"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						<span>Click to expand</span>
					</div>
				)}
			</div>
			<ImageViewModal
				open={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
				}}
				imageUrl={normalizedSrc}
				altText={alt}
			/>
		</>
	);
}
